import React, {
  createContext, useContext, useMemo, useState, useEffect
} from 'react';
import { Container } from 'react-bootstrap';
import { Typography } from 'tfc-components';

import { ReactComponent as Success } from 'assets/icons/ic_check.svg';
import Icon from 'components/atoms/Icon';

interface ToastProps {
  children?: React.ReactNode;
}

type ToastItem = {
  id: string;
  message: string;
  type: 'success' | 'error' | '';
};

type ToastContextValues = {
  toasts: ToastItem[];
  showToast: (message: string, type: 'success' | 'error' | '') => void;
  removeToast: (id: string) => void;
};

const ToastContext = createContext<ToastContextValues>({
  toasts: [],
  showToast: () => {},
  removeToast: () => {},
});

export const ToastProvider: React.FC<ToastProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Generate a unique ID for each toast
  const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

  const showToast = (message: string, type: 'success' | 'error' | '') => {
    const newToast = {
      id: generateId(),
      message,
      type,
    };
    setToasts((prevToasts) => [...prevToasts, newToast]);
  };

  const removeToast = (id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  // Auto-remove toasts after delay
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (toasts.length > 0) {
      const timer = setTimeout(() => {
        setToasts((prevToasts) => {
          // Remove the oldest toast
          const [, ...rest] = prevToasts;
          return rest;
        });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [toasts]);

  const toastProvider = useMemo(
    () => ({
      toasts,
      showToast,
      removeToast,
    }),
    [toasts]
  );

  return (
    <ToastContext.Provider value={toastProvider}>
      {children}

      {/* Global Toast Container */}
      <div className="m-toast-container position-fixed">
        {toasts.map((toast, index) => (
          <div
            key={toast.id}
            className={`m-toast toast align-items-center text-bg-${toast.type} mb-2 show`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            style={{
              opacity: 1,
              zIndex: 1050 + index,
              transform: `translateY(${index * 10}px)`,
            }}
          >
            <Container className="m-toast_body toast-body d-flex align-items-center">
              {(() => {
                switch (toast.type) {
                  case 'success':
                    return <Success className="m-toast_icon-success" />;
                  case 'error':
                    return <Icon iconName="warn" size="24" />;
                  default:
                    return null;
                }
              })()}
              <Typography.Text fontweight="500" extendClasses={`m-toast_text m-toast_text-${toast.type} flex-grow-1`}>
                {toast.message}
              </Typography.Text>
            </Container>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  // Simplified interface to maintain backward compatibility
  return {
    ...context,
    // This wrapper provides the old API format
    showToast: ({
      message,
      type = 'success',
      show = true,
    }: {
      message: string;
      type?: 'success' | 'error' | '';
      show?: boolean;
    }) => {
      if (show) {
        context.showToast(message, type);
      }
    },
  };
};
